import { useState, useEffect } from 'react';
import api from '../api';
import {
  Settings as SettingsIcon,
  Save,
  Plus,
  Trash2,
  RefreshCw,
  Palette,
  Bell,
  Globe,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  X,
  Table2,
  Edit2,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { key: 'tables', label: 'Table Configuration', icon: Table2 },
  { key: 'branding', label: 'Branding & Colors', icon: Palette },
  { key: 'notifications', label: 'Notification Settings', icon: Bell },
  { key: 'general', label: 'General', icon: Globe },
];

// Default settings structure for new installations
const DEFAULT_SETTINGS = {
  branding: [
    { key: 'app_primary_color', value: '#D4AF37', type: 'color', description: 'Primary gold color' },
    { key: 'app_secondary_color', value: '#1F2937', type: 'color', description: 'Secondary dark color' },
    { key: 'app_accent_color', value: '#F59E0B', type: 'color', description: 'Accent color' },
    { key: 'app_background_color', value: '#111827', type: 'color', description: 'Background color' },
  ],
  notifications: [
    { key: 'waitlist_alert_enabled', value: 'true', type: 'boolean', description: 'Enable waitlist alerts' },
    { key: 'waitlist_alert_positions', value: '3', type: 'number', description: 'Alert when X positions away' },
    { key: 'promotional_notifications', value: 'true', type: 'boolean', description: 'Allow promotional push' },
    { key: 'geofence_enabled', value: 'true', type: 'boolean', description: 'Enable geofence notifications' },
    { key: 'push_enabled', value: 'true', type: 'boolean', description: 'Enable push notifications globally' },
  ],
  general: [
    { key: 'maintenance_mode', value: 'false', type: 'boolean', description: 'Enable maintenance mode' },
    { key: 'min_app_version', value: '1.0.0', type: 'string', description: 'Minimum required app version' },
    { key: 'force_update', value: 'false', type: 'boolean', description: 'Force app update' },
  ],
};

function SettingInput({ setting, value, onChange }) {
  if (setting.type === 'color') {
    return (
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-16 rounded cursor-pointer border-0"
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
          placeholder="#000000"
        />
      </div>
    );
  }

  if (setting.type === 'boolean') {
    return (
      <button
        onClick={() => onChange(value === 'true' ? 'false' : 'true')}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          value === 'true' ? 'bg-amber-500' : 'bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            value === 'true' ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    );
  }

  if (setting.type === 'number') {
    return (
      <input
        type="number"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-32 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
      />
    );
  }

  // Default: text/string
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
    />
  );
}

// Data Privacy (PII) redaction policy — mask vs. full hide, per category.
// Applies to staff who lack users:view_contact / users:view_identity.
function PiiAccessCard() {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get('/admin/settings/pii-access')
      .then((res) => { if (active) setPolicy(res.data?.policy || { contact: 'mask', identity: 'hide' }); })
      .catch(() => { if (active) setPolicy({ contact: 'mask', identity: 'hide' }); });
    return () => { active = false; };
  }, []);

  const update = async (cat, value) => {
    const prev = policy;
    setPolicy({ ...policy, [cat]: value });
    setSaving(true);
    try {
      await api.put('/admin/settings/pii-access', { [cat]: value });
      toast.success('Privacy setting saved');
    } catch (e) {
      setPolicy(prev);
      toast.error('Failed to save privacy setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-1">
        <EyeOff className="text-amber-500" size={20} />
        <span className="text-lg font-semibold text-white">Data Privacy (PII)</span>
      </div>
      <p className="text-gray-400 text-sm mb-3">
        Controls how player personal data appears to staff who don't have the matching
        view permission. Owners, admins, and managers always see full details.
      </p>

      <div className="flex items-center justify-between py-2 border-t border-gray-700">
        <div>
          <div className="text-white text-sm font-medium">Contact info (email &amp; phone)</div>
          <div className="text-gray-400 text-xs">Hidden from anyone without “View player contact info.”</div>
        </div>
        <select
          value={policy?.contact || 'mask'}
          disabled={!policy || saving}
          onChange={(e) => update('contact', e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
        >
          <option value="mask">Mask (partial)</option>
          <option value="hide">Hide (fully)</option>
        </select>
      </div>

      <div className="flex items-center justify-between py-2 border-t border-gray-700">
        <div>
          <div className="text-white text-sm font-medium">Identity (birthday &amp; home address)</div>
          <div className="text-gray-400 text-xs">Hidden from anyone without “View player identity.”</div>
        </div>
        <select
          value={policy?.identity || 'hide'}
          disabled={!policy || saving}
          onChange={(e) => update('identity', e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
        >
          <option value="mask">Mask (partial)</option>
          <option value="hide">Hide (fully)</option>
        </select>
      </div>
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [modified, setModified] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['tables', 'branding']);
  const [showNewSetting, setShowNewSetting] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', type: 'string', category: 'general', description: '' });

  // Table Configuration state
  const [tableMappings, setTableMappings] = useState([]);
  const [tableMappingsLoading, setTableMappingsLoading] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [newMapping, setNewMapping] = useState({ tableNumber: '', gameName: '', isActive: true });
  const [showNewMapping, setShowNewMapping] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  // Display settings state
  const [displaySettings, setDisplaySettings] = useState({ hidePlayerCounts: false });
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [reviewCalloutEnabled, setReviewCalloutEnabled] = useState(true);
  const [savingReviewCallout, setSavingReviewCallout] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsRes = await api.get('/admin/settings');
      setSettings(settingsRes.data.settings || {});
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableMappings = async () => {
    try {
      setTableMappingsLoading(true);
      const res = await api.get('/admin/settings/table-games');
      setTableMappings(res.data.mappings || []);
    } catch (err) {
      console.error('Failed to load table mappings:', err);
      toast.error('Failed to load table mappings');
    } finally {
      setTableMappingsLoading(false);
    }
  };

  const fetchDisplaySettings = async () => {
    try {
      const res = await api.get('/settings/display');
      setDisplaySettings({ hidePlayerCounts: res.data.hidePlayerCounts || false });
    } catch (err) {
      console.error('Failed to load display settings:', err);
    }
  };

  const fetchReviewCalloutSetting = async () => {
    try {
      const res = await api.get('/reviews/admin/callout');
      setReviewCalloutEnabled(!!res.data.enabled);
    } catch (err) {
      console.error('Failed to load review callout setting:', err);
    }
  };

  const toggleHidePlayerCounts = async () => {
    try {
      setSavingDisplay(true);
      const newValue = !displaySettings.hidePlayerCounts;
      await api.put('/settings/display', { hidePlayerCounts: newValue });
      setDisplaySettings({ ...displaySettings, hidePlayerCounts: newValue });
      toast.success(newValue ? 'Player counts hidden from app' : 'Player counts visible in app');
    } catch (err) {
      toast.error('Failed to update display setting');
    } finally {
      setSavingDisplay(false);
    }
  };

  const toggleReviewCallout = async () => {
    try {
      setSavingReviewCallout(true);
      const newValue = !reviewCalloutEnabled;
      await api.put('/reviews/admin/callout', { enabled: newValue });
      setReviewCalloutEnabled(newValue);
      toast.success(newValue ? 'Review callout enabled in app' : 'Review callout disabled in app');
    } catch (err) {
      toast.error('Failed to update review callout setting');
    } finally {
      setSavingReviewCallout(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTableMappings();
    fetchDisplaySettings();
    fetchReviewCalloutSetting();
  }, []);

  const handleChange = (key, value) => {
    setModified((prev) => ({ ...prev, [key]: value }));
  };

  const getValue = (key, defaultValue = '') => {
    if (modified[key] !== undefined) return modified[key];
    for (const cat of Object.values(settings)) {
      const found = cat.find((s) => s.key === key);
      if (found) return found.value;
    }
    return defaultValue;
  };

  const handleSave = async () => {
    if (Object.keys(modified).length === 0) {
      toast.error('No changes to save');
      return;
    }

    try {
      setSaving(true);
      const updates = Object.entries(modified).map(([key, value]) => ({ key, value }));
      await api.put('/admin/settings', { settings: updates });
      toast.success('Settings saved successfully');
      setModified({});
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSetting = async () => {
    if (!newSetting.key || !newSetting.value) {
      toast.error('Key and value are required');
      return;
    }
    try {
      await api.post('/admin/settings', newSetting);
      toast.success('Setting created');
      setShowNewSetting(false);
      setNewSetting({ key: '', value: '', type: 'string', category: 'general', description: '' });
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create setting');
    }
  };

  const handleDeleteSetting = async (key) => {
    if (!confirm(`Delete setting "${key}"?`)) return;
    try {
      await api.delete(`/admin/settings/${key}`);
      toast.success('Setting deleted');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to delete setting');
    }
  };

  // Table Mapping handlers
  const handleSaveMapping = async (mapping) => {
    try {
      setSavingMapping(true);
      await api.post('/admin/settings/table-games', mapping);
      toast.success(editingMapping ? 'Mapping updated' : 'Mapping created');
      setEditingMapping(null);
      setShowNewMapping(false);
      setNewMapping({ tableNumber: '', gameName: '', isActive: true });
      fetchTableMappings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save mapping');
    } finally {
      setSavingMapping(false);
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!confirm('Delete this table mapping?')) return;
    try {
      await api.delete(`/admin/settings/table-games/${id}`);
      toast.success('Mapping deleted');
      fetchTableMappings();
    } catch (err) {
      toast.error('Failed to delete mapping');
    }
  };

  const handleToggleMappingActive = async (mapping) => {
    try {
      await api.put(`/admin/settings/table-games/${mapping.id}`, {
        isActive: !mapping.isActive,
      });
      toast.success(`Mapping ${mapping.isActive ? 'disabled' : 'enabled'}`);
      fetchTableMappings();
    } catch (err) {
      toast.error('Failed to update mapping');
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const getSettingsForCategory = (categoryKey) => {
    const existing = settings[categoryKey] || [];
    const defaults = DEFAULT_SETTINGS[categoryKey] || [];
    const existingKeys = existing.map((s) => s.key);
    const combined = [...existing];
    defaults.forEach((d) => {
      if (!existingKeys.includes(d.key)) combined.push(d);
    });
    return combined;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SettingsIcon className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">App Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowNewSetting(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
          >
            <Plus size={18} /> Add Setting
          </button>
          <button onClick={fetchSettings} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RefreshCw size={18} />
          </button>
          <button
            onClick={handleSave}
            disabled={Object.keys(modified).length === 0 || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              Object.keys(modified).length > 0
                ? 'bg-amber-500 text-black hover:bg-amber-400'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={18} />
            {saving ? 'Saving...' : `Save (${Object.keys(modified).length})`}
          </button>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {Object.keys(modified).length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center justify-between">
          <span className="text-amber-400">You have {Object.keys(modified).length} unsaved changes</span>
          <button onClick={() => setModified({})} className="text-gray-400 hover:text-white text-sm">
            Discard changes
          </button>
        </div>
      )}

      {/* Data Privacy (PII) */}
      <PiiAccessCard />

      {/* Display Settings */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-4">
          <Eye className="text-amber-500" size={20} />
          <span className="text-lg font-semibold text-white">Display Settings</span>
        </div>
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="text-white font-medium">Hide Player Counts</p>
            <p className="text-gray-400 text-sm">
              Hides playing counts, table counts, and seat details from the app. Waitlist counts remain visible.
            </p>
          </div>
          <button
            onClick={toggleHidePlayerCounts}
            disabled={savingDisplay}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              displaySettings.hidePlayerCounts ? 'bg-amber-500' : 'bg-gray-600'
            } ${savingDisplay ? 'opacity-50' : ''}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                displaySettings.hidePlayerCounts ? 'translate-x-6' : ''
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between py-2 border-t border-gray-700 mt-2 pt-4">
          <div>
            <p className="text-white font-medium">Show Google Review Callout</p>
            <p className="text-gray-400 text-sm">
              Shows the &ldquo;Leave a review&rdquo; banner on Home and Promos for users who haven&apos;t confirmed a review yet. Toggle off to hide it without an OTA push.
            </p>
          </div>
          <button
            onClick={toggleReviewCallout}
            disabled={savingReviewCallout}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              reviewCalloutEnabled ? 'bg-amber-500' : 'bg-gray-600'
            } ${savingReviewCallout ? 'opacity-50' : ''}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                reviewCalloutEnabled ? 'translate-x-6' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Settings Categories */}
      <div className="space-y-4">
        {CATEGORIES.map(({ key, label, icon: Icon }) => {
          const isExpanded = expandedCategories.includes(key);
          
          // Special handling for Table Configuration category
          if (key === 'tables') {
            return (
              <div key={key} className="bg-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleCategory(key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="text-amber-500" size={20} />
                    <span className="text-lg font-semibold text-white">{label}</span>
                    <span className="text-sm text-gray-500">({tableMappings.length} mappings)</span>
                  </div>
                  {isExpanded ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
                </button>
                
                {isExpanded && (
                  <div className="border-t border-gray-700 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400 text-sm">
                        Configure game names for each table. These override the names from TableCaptain.
                      </p>
                      <button
                        onClick={() => setShowNewMapping(true)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-black rounded-lg hover:bg-amber-400 text-sm font-medium"
                      >
                        <Plus size={16} /> Add Mapping
                      </button>
                    </div>
                    
                    {/* New Mapping Form */}
                    {showNewMapping && (
                      <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                        <h4 className="text-white font-medium">New Table Mapping</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Table Number</label>
                            <input
                              type="text"
                              value={newMapping.tableNumber}
                              onChange={(e) => setNewMapping(prev => ({ ...prev, tableNumber: e.target.value }))}
                              placeholder="e.g., 03, 04, 10"
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Game Name</label>
                            <input
                              type="text"
                              value={newMapping.gameName}
                              onChange={(e) => setNewMapping(prev => ({ ...prev, gameName: e.target.value }))}
                              placeholder="e.g., $2/3 NLH, BJ ELITE 6 DECK"
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setShowNewMapping(false);
                              setNewMapping({ tableNumber: '', gameName: '', isActive: true });
                            }}
                            className="px-3 py-1.5 text-gray-400 hover:text-white"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveMapping(newMapping)}
                            disabled={!newMapping.tableNumber || !newMapping.gameName || savingMapping}
                            className="px-4 py-1.5 bg-amber-500 text-black rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {savingMapping ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Mappings List */}
                    {tableMappingsLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                      </div>
                    ) : tableMappings.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No table mappings configured. Add one to override game names from TableCaptain.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {tableMappings.map((mapping) => (
                          <div
                            key={mapping.id}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              mapping.isActive ? 'bg-gray-700/50' : 'bg-gray-700/20'
                            }`}
                          >
                            {editingMapping?.id === mapping.id ? (
                              // Edit mode
                              <div className="flex-1 flex items-center gap-4">
                                <input
                                  type="text"
                                  value={editingMapping.tableNumber}
                                  onChange={(e) => setEditingMapping(prev => ({ ...prev, tableNumber: e.target.value }))}
                                  className="w-24 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                                />
                                <input
                                  type="text"
                                  value={editingMapping.gameName}
                                  onChange={(e) => setEditingMapping(prev => ({ ...prev, gameName: e.target.value }))}
                                  className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                                />
                                <button
                                  onClick={() => {
                                    handleSaveMapping(editingMapping);
                                  }}
                                  className="p-1 text-green-500 hover:text-green-400"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={() => setEditingMapping(null)}
                                  className="p-1 text-gray-400 hover:text-white"
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            ) : (
                              // View mode
                              <>
                                <div className="flex items-center gap-4">
                                  <span className={`font-mono px-2 py-1 rounded text-sm ${
                                    mapping.isActive ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-600 text-gray-400'
                                  }`}>
                                    Table {mapping.tableNumber}
                                  </span>
                                  <span className={mapping.isActive ? 'text-white' : 'text-gray-500'}>
                                    {mapping.gameName}
                                  </span>
                                  {!mapping.isActive && (
                                    <span className="text-xs text-gray-500 bg-gray-600 px-2 py-0.5 rounded">Disabled</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleToggleMappingActive(mapping)}
                                    className={`p-1.5 rounded ${
                                      mapping.isActive ? 'text-gray-400 hover:text-amber-400' : 'text-gray-500 hover:text-green-400'
                                    }`}
                                    title={mapping.isActive ? 'Disable' : 'Enable'}
                                  >
                                    {mapping.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                                  </button>
                                  <button
                                    onClick={() => setEditingMapping({ ...mapping })}
                                    className="p-1.5 text-gray-400 hover:text-amber-400"
                                    title="Edit"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteMapping(mapping.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-400"
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }
          
          // Regular category handling
          const categorySettings = getSettingsForCategory(key);
          
          return (
            <div key={key} className="bg-gray-800 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(key)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-3">
                  <Icon className="text-amber-500" size={20} />
                  <span className="text-lg font-semibold text-white">{label}</span>
                  <span className="text-sm text-gray-500">({categorySettings.length})</span>
                </div>
                {isExpanded ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
              </button>
              
              {isExpanded && (
                <div className="border-t border-gray-700 divide-y divide-gray-700">
                  {categorySettings.map((setting) => (
                    <div key={setting.key} className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium truncate">{setting.key}</p>
                          {setting.isPublic && <Eye size={14} className="text-green-400" title="Public" />}
                        </div>
                        {setting.description && (
                          <p className="text-sm text-gray-500 truncate">{setting.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <SettingInput
                          setting={setting}
                          value={getValue(setting.key, setting.value)}
                          onChange={(v) => handleChange(setting.key, v)}
                        />
                        <button
                          onClick={() => handleDeleteSetting(setting.key)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New Setting Modal */}
      {showNewSetting && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Add New Setting</h2>
              <button onClick={() => setShowNewSetting(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Key</label>
                <input
                  value={newSetting.key}
                  onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="setting_key"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Value</label>
                <input
                  value={newSetting.value}
                  onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Type</label>
                  <select
                    value={newSetting.type}
                    onChange={(e) => setNewSetting({ ...newSetting, type: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="string">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="color">Color</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={newSetting.category}
                    onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input
                  value={newSetting.description}
                  onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <button
                onClick={handleCreateSetting}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400"
              >
                Create Setting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
